import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { useCallback, useEffect, useState } from 'react';

import { CardListSelect } from '../../../../shared/ui/card-list';
import { useReportFilters } from '../../../reports/hooks/useReportFilters';
import { useSavedQueries } from '../../api/use-saved-queries';
import { FilterModal } from '../../filters/components/FilterModal';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { getFilterDefinition } from '../../filters/engine/filterRegistry';
import { type Filter, FilterId, FilterType } from '../../filters/types/filters';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import type { ReportParameterConfig } from '../PageConfigProvider/page-reducer';
import type {
  PositionedItem,
  SnowflakeConfig,
  SnowflakeConfigField,
} from '../PageConfigProvider/types/item';
import { EditParameterModal } from '../Reports/Parameters/EditParameterModal';
import { ManualOverrideModal } from '../Reports/Parameters/ParameterEntitiesEditor';
import { toReadableFilterName } from '../Reports/Parameters/parameters';
import { useAddParameterFilterGroup } from '../Reports/Parameters/useAddParameterFilterGroup';
import {
  describeFieldBinding,
  getCompatibleParameters,
} from './utils/auto-map-fields-to-filters';
import { filterSavedQueriesForChart } from './utils/saved-query-for-chart';

function resolveParameterForField(
  field: SnowflakeConfigField,
  reportParameters: ReportParameterConfig[]
): ReportParameterConfig | undefined {
  if (!field.parameterId) return undefined;
  return reportParameters.find((p) => p.parameterId === field.parameterId);
}

type FieldItemMenuProps = {
  isBound: boolean;
  hasCustomValue: boolean;
  compatibleParams: ReportParameterConfig[];
  onBind: (parameterId: string) => void;
  onEdit: () => void;
  onUnbind: () => void;
  onManualOverride: () => void;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

const FieldItemMenu = ({
  isBound,
  hasCustomValue,
  compatibleParams,
  onBind,
  onEdit,
  onUnbind,
  onManualOverride,
  isOpen,
  onOpen,
  onClose,
}: FieldItemMenuProps) => (
  <Menu
    placement="bottom-end"
    offset={[0, 0]}
    {...(isOpen !== undefined && { isOpen, onOpen, onClose })}
  >
    <MenuButton
      as={IconButton}
      icon={<IconDotGrid1x3Vertical size={14} />}
      size="sm"
      aria-label="Actions"
      variant="ghost"
      color="green.600"
      bg="green.100"
      visibility="hidden"
      _active={{ color: 'green.600', bg: 'green.100' }}
      _groupHover={{
        visibility: 'visible',
        color: 'green.600',
        bg: 'green.100',
      }}
      onClick={(e) => e.stopPropagation()}
    />
    <Portal>
      <MenuList>
        {isBound || hasCustomValue ? (
          <>
            {isBound && (
              <>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  Edit Parameter
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onManualOverride();
                  }}
                >
                  Replace with Custom Value
                </MenuItem>
                <MenuDivider />
              </>
            )}
            {hasCustomValue && !isBound && (
              <>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onManualOverride();
                  }}
                >
                  Edit Custom Value
                </MenuItem>
                <MenuDivider />
              </>
            )}
            <MenuItem
              color="red"
              _focus={{ bg: 'red.100' }}
              onClick={(e) => {
                e.stopPropagation();
                onUnbind();
              }}
            >
              Clear Field
            </MenuItem>
          </>
        ) : (
          <>
            {compatibleParams.map((p) => (
              <MenuItem
                key={p.parameterId}
                onClick={(e) => {
                  e.stopPropagation();
                  onBind(p.parameterId);
                }}
              >
                {p.name}
              </MenuItem>
            ))}
            {compatibleParams.length > 0 && <MenuDivider />}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onManualOverride();
              }}
            >
              Set custom value
            </MenuItem>
          </>
        )}
      </MenuList>
    </Portal>
  </Menu>
);

type SnowflakeParameterListProps = {
  item: PositionedItem;
  isDisabled?: boolean;
};

type FieldSection = { heading: string; fields: SnowflakeConfigField[] };

function splitFields(fields: SnowflakeConfigField[]): FieldSection[] {
  const required = fields.filter((f) => f.required !== false);
  const optional = fields.filter((f) => f.required === false);
  const sections: FieldSection[] = [];
  if (required.length > 0) {
    sections.push({ heading: 'Required', fields: required });
  }
  if (optional.length > 0) {
    sections.push({ heading: 'Optional', fields: optional });
  }
  return sections;
}

export const SnowflakeParameterList = ({
  item,
  isDisabled = false,
}: SnowflakeParameterListProps) => {
  const { dispatch, parentId } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { activeFilterMap, filterState } = useFilterContext();
  const { getReadableValue } = useReportFilters();
  const reportParameters =
    pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];

  const snowflakeConfig = item?.plot?.snowflakeConfig;
  const savedQueryId = snowflakeConfig?.savedQueryId ?? '';
  const chartType = item?.plot?.chartType;
  const fields = snowflakeConfig?.fields ?? [];

  const { data: savedQueries, isLoading: savedQueriesLoading } =
    useSavedQueries();
  const availableSavedQueries = filterSavedQueriesForChart({
    queries: savedQueries,
    chartType,
    parentId,
  });
  const selectedQuery = savedQueries?.find((q) => q.id === savedQueryId);
  const isSelectedQueryAvailable = availableSavedQueries.some(
    (query) => query.id === savedQueryId
  );

  useEffect(() => {
    if (savedQueriesLoading) {
      return;
    }
    if (savedQueryId === '' || isSelectedQueryAvailable) {
      return;
    }
    dispatch({
      type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG',
      payload: {
        id: item.id,
        snowflakeConfig: { savedQueryId: '', fields: [] },
        selectorOverrides: undefined,
      },
    });
  }, [
    dispatch,
    isSelectedQueryAvailable,
    item.id,
    savedQueriesLoading,
    savedQueryId,
  ]);

  const boundParameters = fields
    .map((f) => resolveParameterForField(f, reportParameters))
    .filter((p): p is ReportParameterConfig => p !== undefined);

  useAddParameterFilterGroup({ parameters: boundParameters });

  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isManualOverrideOpen,
    onOpen: onManualOverrideOpen,
    onClose: onManualOverrideClose,
  } = useDisclosure();
  const [activeFieldName, setActiveFieldName] = useState<string | null>(null);
  const [draftParam, setDraftParam] = useState<ReportParameterConfig | null>(
    null
  );
  const [menuOpenField, setMenuOpenField] = useState<string | null>(null);

  const activeField = fields.find((f) => f.name === activeFieldName) ?? null;
  const activeParameter = activeField
    ? resolveParameterForField(activeField, reportParameters)
    : null;

  const filterForModal: Filter | null = (() => {
    if (!activeParameter) return null;
    const filterMapToUse = activeParameter.isPrimary
      ? activeFilterMap
      : filterState.groupMap.get(activeParameter.parameterId);
    if (!filterMapToUse) return null;

    const current =
      filterMapToUse.get(activeParameter.filterId) ??
      getFilterDefinition(activeParameter.filterId);
    return current
      ? { ...current, isPrimary: activeParameter.isPrimary }
      : null;
  })();

  const isParameterSelected = useCallback(
    (parameter: ReportParameterConfig): boolean => {
      const filterMapToUse = parameter.isPrimary
        ? activeFilterMap
        : filterState.groupMap?.get(parameter.parameterId);
      if (!filterMapToUse) return false;

      const filter = filterMapToUse.get(parameter.filterId);
      if (!filter) return false;

      if (filter.type === FilterType.TREE) {
        if (parameter.isPrimary) {
          return (filter.primarySelections ?? []).some((s) => s.isActive);
        }
        return (filter.selections ?? []).length > 0;
      }
      if (
        filter.type === FilterType.LIST ||
        filter.type === FilterType.STATIC_LIST
      ) {
        return (filter.selections ?? []).length > 0;
      }
      if (filter.type === FilterType.VALUE) {
        const val = filter.value;
        return val !== null && val !== undefined && String(val).length > 0;
      }
      return true;
    },
    [filterState, activeFilterMap]
  );

  const handleBind = (fieldName: string, parameterId: string) => {
    if (!snowflakeConfig || isDisabled) return;
    const updatedFields = fields.map((f) =>
      f.name === fieldName
        ? {
            ...f,
            parameterId,
            bindingMode: 'report_parameter' as const,
            value: '',
          }
        : f
    );
    dispatch({
      type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG',
      payload: {
        id: item.id,
        snowflakeConfig: {
          ...snowflakeConfig,
          fields: updatedFields,
        } satisfies SnowflakeConfig,
      },
    });
  };

  const handleUnbind = (fieldName: string) => {
    if (!snowflakeConfig || isDisabled) return;
    const updatedFields = fields.map((f) =>
      f.name === fieldName
        ? {
            ...f,
            parameterId: undefined,
            bindingMode: 'static' as const,
            value: '',
          }
        : f
    );
    dispatch({
      type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG',
      payload: {
        id: item.id,
        snowflakeConfig: {
          ...snowflakeConfig,
          fields: updatedFields,
        } satisfies SnowflakeConfig,
      },
    });
  };

  const handleParameterSubmit = (param: ReportParameterConfig) => {
    dispatch({
      type: 'UPDATE_REPORT_PARAMETER',
      payload: {
        parameterId: param.parameterId,
        updates: {
          name: param.name,
          filterId: param.filterId,
          isPrimary: param.isPrimary,
          selectionLimit: param.selectionLimit,
        },
      },
    });
  };

  const handleManualOverrideSave = (value: string) => {
    if (!snowflakeConfig || !activeFieldName) return;
    const updatedFields = fields.map((f) =>
      f.name === activeFieldName
        ? {
            ...f,
            value,
            bindingMode: 'static' as const,
            parameterId: undefined,
          }
        : f
    );
    dispatch({
      type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG',
      payload: {
        id: item.id,
        snowflakeConfig: {
          ...snowflakeConfig,
          fields: updatedFields,
        } satisfies SnowflakeConfig,
      },
    });
  };

  const boundParameterIds = new Set(
    fields.map((f) => f.parameterId).filter((id): id is string => !!id)
  );

  const sections = splitFields(fields);

  const showParametersBlock = fields.length > 0;

  return (
    <Box marginBottom="1.5rem">
      {!savedQueryId && !savedQueriesLoading && (
        <Text fontSize="sm" color="gray.600" marginBottom={3}>
          Select a custom variable under Variable to attach a saved query to
          this chart.
        </Text>
      )}
      {selectedQuery && selectedQuery.fields.length === 0 && (
        <Text fontSize="sm" color="gray.600">
          This query has no fields. Save and the query will run with no
          parameters.
        </Text>
      )}
      {showParametersBlock && (
        <>
          <Text fontSize="sm" fontWeight={600} marginBottom="0.5rem">
            Parameters
          </Text>

          {sections.map((section) => (
            <Box
              key={section.heading}
              mb={section.heading === 'Required' && sections.length > 1 ? 3 : 0}
            >
              {sections.length > 1 && (
                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="gray.500"
                  mb={1.5}
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  {section.heading}
                </Text>
              )}
              <VStack spacing="10px" alignItems="stretch">
                {section.fields.map((field) => {
                  const parameter = resolveParameterForField(
                    field,
                    reportParameters
                  );
                  const binding = describeFieldBinding(field, reportParameters);
                  const isBound = parameter !== undefined;
                  const isStatic = field.bindingMode === 'static';
                  const customValue = field.value?.trim() ?? '';
                  const hasCustomValue = customValue.length > 0;
                  const compatibleParams = getCompatibleParameters(
                    field,
                    reportParameters
                  ).filter(
                    (p) =>
                      !boundParameterIds.has(p.parameterId) ||
                      p.parameterId === field.parameterId
                  );

                  const detail = (() => {
                    if (!isBound) {
                      if (hasCustomValue) return `Value: ${customValue}`;
                      return binding.label;
                    }
                    return (
                      getReadableValue({
                        ...(!parameter.isPrimary && {
                          parameterKey: parameter.parameterId,
                        }),
                        filterId: parameter.filterId,
                      }) || 'None Selected'
                    );
                  })();

                  const slotLabel = (() => {
                    if (field.slotRole && isBound) {
                      return `${field.label}: ${toReadableFilterName(parameter.filterId)}`;
                    }
                    return field.label;
                  })();

                  const handleCardClick = () => {
                    if (isDisabled) return;
                    if (isBound) {
                      setActiveFieldName(field.name);
                      onFilterOpen();
                      return;
                    }
                    if (isStatic) {
                      setActiveFieldName(field.name);
                      onManualOverrideOpen();
                      return;
                    }
                    if (compatibleParams.length > 0) {
                      setMenuOpenField(field.name);
                      return;
                    }
                    setMenuOpenField(field.name);
                  };

                  return (
                    <CardListSelect<string>
                      key={field.name}
                      id={field.name}
                      label={slotLabel}
                      detail={detail}
                      variant="report"
                      isChecked={
                        isBound
                          ? isParameterSelected(parameter)
                          : hasCustomValue
                      }
                      onChange={handleCardClick}
                      cta={
                        <FieldItemMenu
                          isBound={isBound}
                          hasCustomValue={hasCustomValue}
                          compatibleParams={compatibleParams}
                          onBind={(parameterId) =>
                            handleBind(field.name, parameterId)
                          }
                          onEdit={() => {
                            if (parameter) {
                              setActiveFieldName(field.name);
                              setDraftParam(parameter);
                              onEditOpen();
                            }
                          }}
                          onUnbind={() => handleUnbind(field.name)}
                          onManualOverride={() => {
                            setActiveFieldName(field.name);
                            setMenuOpenField(null);
                            onManualOverrideOpen();
                          }}
                          isOpen={menuOpenField === field.name}
                          onOpen={() => setMenuOpenField(field.name)}
                          onClose={() => setMenuOpenField(null)}
                        />
                      }
                    />
                  );
                })}
              </VStack>
            </Box>
          ))}

          {activeParameter && (
            <FilterModal
              isOpen={isFilterOpen}
              onClose={onFilterClose}
              title={activeParameter.name}
              filter={filterForModal}
              selectedFilterId={activeParameter.filterId}
              isCentered
              selectionLimit={activeParameter.selectionLimit ?? 1}
              {...(!activeParameter.isPrimary && {
                groupKeyOverride: activeParameter.parameterId,
              })}
              {...(activeParameter.filterId === FilterId.COMPANY && {
                disabledLevels: [0, 1],
              })}
            />
          )}

          {draftParam && activeFieldName && (
            <EditParameterModal
              key={`${item.id}-${activeFieldName}`}
              isOpen={isEditOpen}
              onClose={onEditClose}
              mode="edit"
              initial={draftParam}
              onSubmit={handleParameterSubmit}
              isChartLevel
            />
          )}

          {activeFieldName && (
            <ManualOverrideModal
              isOpen={isManualOverrideOpen}
              onClose={onManualOverrideClose}
              fieldLabel={
                fields.find((f) => f.name === activeFieldName)?.label ??
                activeFieldName
              }
              initialValue={
                fields.find((f) => f.name === activeFieldName)?.value ?? ''
              }
              onSave={handleManualOverrideSave}
            />
          )}
        </>
      )}
    </Box>
  );
};
